import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import CampoSenha from '../components/CampoSenha'

const loginSchema = z.object({
  email: z.string().email('E-mail invalido'),
  senha: z.string().min(1, 'Senha obrigatoria'),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  async function onSubmit(data: LoginForm) {
    try {
      await login(data.email, data.senha)
      navigate('/dashboard')
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ?? 'Erro ao realizar login. Tente novamente.'
      setError('root', { message: msg })
    }
  }

  return (
    <div className="min-h-[100dvh] bg-compumaq-preto flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src="/compumaq-logo.jpg"
              alt="Compumaq Tecnologia"
              className="w-16 h-16 rounded-2xl mb-4 mx-auto object-cover"
            />
            <h1 className="text-2xl font-bold text-compumaq-texto">
              Campeonatos de Tiro
            </h1>
            <p className="text-sm text-compumaq-textoSuave mt-1">
              Sistema de Gestao de Campeonatos
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Erro geral */}
            {errors.root && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{errors.root.message}</p>
              </div>
            )}

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-compumaq-textoSuave mb-1"
              >
                E-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm text-compumaq-texto bg-compumaq-preto transition-colors focus:outline-none focus:ring-2 focus:ring-compumaq-dourado ${
                  errors.email ? 'border-red-400' : 'border-compumaq-dourado/25 hover:border-compumaq-dourado/50'
                }`}
                placeholder="seu@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Senha */}
            <div>
              <label
                htmlFor="senha"
                className="block text-sm font-medium text-compumaq-textoSuave mb-1"
              >
                Senha
              </label>
              <CampoSenha
                id="senha"
                autoComplete="current-password"
                {...register('senha')}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm text-compumaq-texto bg-compumaq-preto transition-colors focus:outline-none focus:ring-2 focus:ring-compumaq-dourado ${
                  errors.senha ? 'border-red-400' : 'border-compumaq-dourado/25 hover:border-compumaq-dourado/50'
                }`}
                placeholder="••••••••"
              />
              {errors.senha && (
                <p className="mt-1 text-xs text-red-400">{errors.senha.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-compumaq-dourado text-compumaq-texto py-2.5 rounded-lg font-semibold text-sm hover:bg-compumaq-doradoClaro transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>

            <Link to="/esqueci-senha" className="block text-center text-xs text-compumaq-doradoClaro hover:underline">
              Esqueci minha senha
            </Link>
          </form>
        </div>

        <p className="text-center text-xs text-compumaq-textoSuave mt-6">
          Sistema de Gestao de Campeonatos de Tiro Esportivo
        </p>
      </div>
    </div>
  )
}
